import { useState, useRef, useEffect } from 'react';
import type { VendorTenant } from '@/lib/vendorApi';

interface Props {
  tenants:       VendorTenant[];
  onAction:      (tenant: VendorTenant, action: Action) => void;
}

export type Action = 'generate-key' | 'extend' | 'reset-demo' | 'revoke' | 'set-tier' | 'hard-delete';

const PLAN_LABELS: Record<string, string> = {
  demo:    'Demo',
  onprem:  'On-Premise',
  cloud:   'Cloud',
  expired: 'Expired',
};

function StatusBadge({ tenant }: { tenant: VendorTenant }) {
  const { plan, demo_ends_at, license_expires_at, license_state, revoked_at } = tenant;
  // Revoked tenants show how long ago — gives the admin context to decide
  // whether they've waited long enough to feel safe hard-deleting.
  if (revoked_at) {
    const days = Math.floor((Date.now() - new Date(revoked_at).getTime()) / 86400_000);
    const label = days === 0 ? 'today' : days === 1 ? '1d ago' : `${days}d ago`;
    return <span className="text-red-400 text-xs font-medium">Revoked · {label}</span>;
  }
  if (license_state.status === 'blocked') {
    return <span className="text-red-400 text-xs font-medium">Expired</span>;
  }
  if (plan === 'demo' && demo_ends_at) {
    const days = Math.ceil((new Date(demo_ends_at).getTime() - Date.now()) / 86400_000);
    return <span className="text-slate-400 text-xs">Demo · {days}d left</span>;
  }
  if (plan === 'onprem' && license_state.graceDays !== undefined) {
    return <span className="text-amber-400 text-xs font-medium">Grace · {license_state.graceDays}d</span>;
  }
  if (plan === 'onprem' && license_expires_at) {
    const days = Math.ceil((new Date(license_expires_at).getTime() - Date.now()) / 86400_000);
    if (days <= 30) return <span className="text-amber-400 text-xs">Expiring · {days}d</span>;
  }
  return <span className="text-emerald-400 text-xs font-medium">Active</span>;
}

function ActionMenu({ tenant, onAction }: { tenant: VendorTenant; onAction: Props['onAction'] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function close(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const canGenerateKey = tenant.plan !== 'cloud';
  const canHardDelete  = tenant.plan === 'expired';

  function act(action: Action) {
    setOpen(false);
    onAction(tenant, action);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-slate-400 hover:text-slate-200 px-2 py-1 rounded text-lg leading-none"
      >
        ⋯
      </button>
      {open && (
        <div className="absolute right-0 top-8 bg-slate-700 border border-slate-600 rounded-lg shadow-xl z-10 py-1 w-40">
          <button
            onClick={() => act('generate-key')}
            disabled={!canGenerateKey}
            className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Generate Key
          </button>
          <button
            onClick={() => act('extend')}
            className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-600"
          >
            Extend
          </button>
          <button
            onClick={() => act('reset-demo')}
            className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-600"
          >
            Reset Demo
          </button>
          <button
            onClick={() => act('set-tier')}
            className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-600"
          >
            Change Tier
          </button>
          <div className="border-t border-slate-600 my-1" />
          <button
            onClick={() => act('revoke')}
            className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-slate-600"
          >
            Revoke
          </button>
          {/* Hard delete is gated to already-revoked tenants so an accidental
              click can never nuke a paying customer. Two-step process: Revoke
              first (soft, reversible), then Delete (irreversible). */}
          <button
            onClick={() => act('hard-delete')}
            disabled={!canHardDelete}
            title={canHardDelete ? undefined : 'Revoke the tenant first'}
            className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Delete…
          </button>
        </div>
      )}
    </div>
  );
}

export function TenantTable({ tenants, onAction }: Props) {
  if (tenants.length === 0) {
    return <p className="text-slate-500 text-sm text-center py-12">No tenants yet.</p>;
  }

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700">
            {['Company', 'Plan', 'Tier', 'Status', 'Users', 'Renewal Date', 'Billing Email', ''].map(h => (
              <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tenants.map(t => (
            <tr key={t.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
              <td className="px-4 py-3 text-slate-100 font-medium">{t.name}</td>
              <td className="px-4 py-3 text-slate-400">{PLAN_LABELS[t.plan] ?? t.plan}</td>
              <td className="px-4 py-3">
                {t.plan === 'onprem'
                  ? <span className="text-slate-500 text-xs">—</span>
                  : <span className={`text-xs font-medium ${t.tier === 'pro' ? 'text-indigo-400' : 'text-slate-400'}`}>
                      {t.tier === 'pro' ? 'Pro' : 'Standard'}
                    </span>}
              </td>
              <td className="px-4 py-3"><StatusBadge tenant={t} /></td>
              <td className="px-4 py-3 text-slate-400">{t.user_count}</td>
              <td className="px-4 py-3 text-slate-400 text-xs">
                {t.plan === 'demo' && t.demo_ends_at
                  ? new Date(t.demo_ends_at).toLocaleDateString()
                  : t.license_expires_at
                    ? new Date(t.license_expires_at).toLocaleDateString()
                    : '—'}
              </td>
              {/* Show billing email for paid customers, otherwise fall back
                  to the admin signup email so demo tenants are identifiable.
                  The greyed style with "(signup)" suffix distinguishes the
                  two so the admin can tell at a glance whether this is a
                  real billing contact or just the trialist's address. */}
              <td className="px-4 py-3 text-xs">
                {t.billing_email ? (
                  <span className="text-slate-300">{t.billing_email}</span>
                ) : t.admin_email ? (
                  <span className="text-slate-500">{t.admin_email} <span className="text-slate-600">(signup)</span></span>
                ) : (
                  <span className="text-slate-600">—</span>
                )}
              </td>
              <td className="px-4 py-3">
                <ActionMenu tenant={t} onAction={onAction} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
